import { Fragment, useState } from "react";
import { Popover, Transition } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";


import { MetamaskWalletProvider } from "../Providers/MetamaskWalletProvider";
import { SolanaTransferProvider } from "../Providers/SolanaTransferProvider";
import { SolanaWalletModalProvider } from "../Providers/SolanaWalletModalProvider";
import { SolanaWalletButton } from "./SolanaWalletButton";
import { LandingPage } from "./LandingPage";
import { NewSwappingPage } from "./NewSwappingPage";
import { NewSwappingResultPage } from "./NewSwappingResultPage";
import { HistoryPage } from "./HistoryPage";
import { useAuth } from "../Contexts/useAuth";

import logo_black from "../assets/Neopets Logo Black.svg";

const headerHavigation = [
    { name: "FAQ", href: process.env.REACT_APP_FAQ_URL },
    { name: "Get Support", href: process.env.REACT_APP_SUPPORT_URL },
    { name: "Join Discord", href: process.env.REACT_APP_DISCORD_URL },
];

const footerNavigation = [
    { name: "FAQ", href: process.env.REACT_APP_FAQ_URL },
    { name: "Get Support", href: process.env.REACT_APP_SUPPORT_URL },
    { name: "Join Discord", href: process.env.REACT_APP_DISCORD_URL },
    { name: "Privacy Policy", href: process.env.REACT_APP_PRIVACY_URL },
    { name: "Terms and Conditions", href: process.env.REACT_APP_TNC_URL },
];

function NormalPageLayout({ children }) {
    return (
        <SolanaWalletModalProvider>
            <div className="bg-sand">
                <div className="relative flex min-h-screen flex-col justify-between overflow-hidden">
                    <Popover
                        as="header"
                        className="container relative z-20 mx-auto max-w-7xl"
                    >
                        <div className="py-3">
                            <nav
                                className="relative flex items-center justify-between px-6"
                                aria-label="Global"
                            >
                                <div className="flex flex-1 items-center">
                                    <div className="flex w-full items-center justify-between md:w-auto">
                                        <a
                                            href="https://www.neopetsmeta.io/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <span className="sr-only">
                                                Neopets Metaverse
                                            </span>
                                            <img
                                                className="h-12 w-auto sm:h-16"
                                                src={logo_black}
                                                alt="Neopets Metaverse Logo"
                                                width="139"
                                                heigh="64"
                                            />
                                        </a>
                                        <div className="-mr-2 flex items-center md:hidden">
                                            <Popover.Button
                                                onClick={() => {
                                                    document.body.style.overflow =
                                                        "hidden";
                                                }}
                                                className="focus-ring-inset inline-flex items-center justify-center rounded-md p-2 text-gray-800 hover:text-gray-900 focus:outline-none"
                                            >
                                                <span className="sr-only">
                                                    Open main menu
                                                </span>
                                                <Bars3Icon
                                                    className="h-6 w-6"
                                                    aria-hidden="true"
                                                />
                                            </Popover.Button>
                                        </div>
                                    </div>
                                </div>
                                <div className="hidden md:flex md:items-center md:space-x-8">
                                    {headerHavigation.map((item) => (
                                        <a
                                            key={item.name}
                                            href={item.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-base font-bold text-gray-800 hover:text-gray-900"
                                        >
                                            {item.name}
                                        </a>
                                    ))}
                                    <SolanaWalletButton />
                                </div>
                            </nav>
                        </div>

                        <Transition
                            as={Fragment}
                            enter="duration-150 ease-out"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="duration-100 ease-in"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Popover.Panel
                                focus
                                className="absolute inset-x-0 top-0 origin-top transform p-0 transition md:hidden"
                            >
                                {({ close }) => (
                                    <div className="h-screen overflow-hidden bg-white shadow-md ring-1 ring-black ring-opacity-5">
                                        <div className="flex items-center justify-between px-6 py-4">
                                            <div>
                                                <img
                                                    className="h-10 w-auto"
                                                    src={logo_black}
                                                    alt="Neopets Metaverse Logo"
                                                />
                                            </div>
                                            <div className="-mr-2">
                                                <button
                                                    onClick={(e) => {
                                                        document.body.style.overflow =
                                                            "auto";
                                                        close(e);
                                                    }}
                                                    className="inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 focus:outline-none"
                                                >
                                                    <span className="sr-only">
                                                        Close menu
                                                    </span>
                                                    <XMarkIcon
                                                        className="h-6 w-6"
                                                        aria-hidden="true"
                                                    />
                                                </button>
                                            </div>
                                        </div>
                                        <div className=" pt-5 pb-6">
                                            <div className="flex flex-col px-3">
                                                {headerHavigation.map(
                                                    (item) => (
                                                        <div
                                                            className="px-2"
                                                            key={`${item.name} - mobile`}
                                                        >
                                                            <a
                                                                href={item.href}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="block border-b px-1 py-4 text-lg font-medium text-gray-800 hover:bg-gray-100"
                                                            >
                                                                {item.name}
                                                            </a>
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                            <div className="mt-6 px-6">
                                                <SolanaWalletButton />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </Popover.Panel>
                        </Transition>
                    </Popover>
                    <div className="container relative z-10 mx-auto flex max-w-7xl grow justify-center">
                        {children}
                    </div>
                    <footer
                        className="bg-footergray"
                        aria-labelledby="footer-heading"
                    >
                        <div className="container relative z-20 mx-auto max-w-7xl">
                            <div className="px-6 py-6 md:flex md:items-center md:justify-between md:py-10">
                                <div className="flex flex-wrap gap-y-2 md:order-2">
                                    {footerNavigation.map((item) => (
                                        <a
                                            key={`footer-${item.name}`}
                                            href={item.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block flex-shrink-0 px-2 py-2 text-sm leading-4 text-white last:border-r-0 hover:text-gray-100 md:border-r md:border-gray-500 md:px-5 md:py-0"
                                        >
                                            {item.name}
                                        </a>
                                    ))}
                                </div>
                                <p className="mt-6 text-sm leading-5 text-white md:order-1 md:mt-0">
                                    Copyright &copy; 2023 neopets
                                </p>
                            </div>
                        </div>
                    </footer>
                </div>
            </div>
        </SolanaWalletModalProvider>
    );
}

export function Content() {
    const { authed } = useAuth();

    if (!authed) {
        return (
            <NormalPageLayout>
                <LandingPage />
            </NormalPageLayout>
        );
    } else {
        return (
            <MetamaskWalletProvider>
                <SolanaTransferProvider>
                    <Page />
                </SolanaTransferProvider>
            </MetamaskWalletProvider>
        );
    }
}

function Page() {
    const [isNewSwappingPage, setIsNewSwappingPage] = useState(false);
    const [isNewSwappingResultPage, setIsNewSwappingResultPage] =
        useState(false);

    if (isNewSwappingPage) {
        return (
            <div className="bg-sand">
                <div className="relative flex min-h-screen">
                    <NewSwappingPage
                        closeNewSwappingPage={() => {
                            setIsNewSwappingPage(false);
                        }}
                        openNewSwappingResultPage={() => {
                            setIsNewSwappingPage(false);
                            setIsNewSwappingResultPage(true);
                        }}
                    />
                </div>
            </div>
        );
    } else if (isNewSwappingResultPage) {
        return (
            <NormalPageLayout>
                <NewSwappingResultPage
                    closeNewSwappingResultPage={() => {
                        setIsNewSwappingResultPage(false);
                    }}
                />
            </NormalPageLayout>
        );
    } else {
        return (
            <NormalPageLayout>
                <HistoryPage
                    openNewSwappingPage={() => {
                        setIsNewSwappingPage(true);
                    }}
                />
            </NormalPageLayout>
        );
    }
}
